/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import {
  contentApi,
  uploadApi,
  renditionsApi,
  versionsApi,
} from "../AlfrescoApi.js";
import errorService from "./ErrorService.js";
import retry from "async-retry";
import { useConfigStore } from "@/stores/config.js";
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import { useUploadStore } from "@/stores/upload.js";
import i18n from "@/i18n/index.js";

import QuickLRU from "quick-lru";

const t = i18n.global.t;

/**
 * Service class for managing files and renditions in Alfresco.
 */
class AlfrescoFileService {
  constructor() {
    this.lruUrls = new QuickLRU({
      maxSize: 100,
      maxAge: 5 * 60 * 1000, // 5m
    });
    // Deduplication caches for rendition requests
    this.pendingRenditions = new Set(); // nodeIds currently being processed
    this.failedRenditions = new QuickLRU({
      maxSize: 500,
      maxAge: 5 * 60 * 1000, // 5 minutes TTL for failed attempts
    });
  }
  getThumbnailUrl(nodeId) {
    const cacheKey = `thumbnail_${nodeId}`;
    if (this.lruUrls.has(cacheKey)) {
      return this.lruUrls.get(cacheKey);
    }
    const url = contentApi.getDocumentThumbnailUrl(nodeId);
    this.lruUrls.set(cacheKey, url);
    return url;
  }

  getPreviewUrl(nodeId) {
    const cacheKey = `preview_${nodeId}`;
    if (this.lruUrls.has(cacheKey)) {
      return this.lruUrls.get(cacheKey);
    }
    const url = contentApi.getDocumentPreviewUrl(nodeId);
    this.lruUrls.set(cacheKey, url);
    return url;
  }

  getContentUrl(nodeId) {
    const cacheKey = `content_${nodeId}`;
    if (this.lruUrls.has(cacheKey)) {
      return this.lruUrls.get(cacheKey);
    }
    const url = contentApi.getContentUrl(nodeId);
    this.lruUrls.set(cacheKey, url);
    return url;
  }

  getVersionContentUrl(nodeId, versionId) {
    const cacheKey = `version_content_${nodeId}_${versionId}`;
    if (this.lruUrls.has(cacheKey)) {
      return this.lruUrls.get(cacheKey);
    }
    const url = contentApi.getVersionContentUrl(nodeId, versionId);
    this.lruUrls.set(cacheKey, url);
    return url;
  }

  uploadFile(nodeId, opts, file) {
    return uploadApi
      .uploadFile(file, null, nodeId, null, opts)
      .on("progress", (progress) => {
        // console.log("Total :" + progress.total);
        // console.log("Loaded :" + progress.loaded);
        console.log("Percent :" + progress.percent);
      })
      .then((data) => {
        this.createMissingRenditions(data.entry.id);
        return data;
      })
      .catch((error) => {
        errorService.uploadFileError(error, file.name);
      });
  }

  createRendition(nodeId, rendition) {
    return this._createRendition(nodeId, {
      id: rendition,
    });
  }

  createAllRendition(nodeId) {
    return this._createRendition(nodeId, [
      {
        id: "doclib",
      },
      {
        id: "imgpreview",
      },
    ]);
  }

  /**
   * Request creation of the wanted renditions, but only those that are both
   * applicable to the node (present in listRenditions) and not yet created.
   *
   * Unlike {@link createAllRendition}, this never asks Alfresco to transform a
   * type that cannot produce the rendition (archives, binaries, ...), avoiding
   * the failed createRendition calls that polluted the logs.
   *
   * @param nodeId
   * @param wanted rendition ids to ensure, default ["doclib", "imgpreview"]
   * @returns {Promise<*>}
   */
  async createMissingRenditions(nodeId, wanted = ["doclib", "imgpreview"]) {
    const paging = await renditionsApi.listRenditions(nodeId);
    const statusById = new Map(
      (paging?.list?.entries ?? []).map((e) => [e.entry.id, e.entry.status]),
    );
    // Keep only renditions that are possible for this node and still missing.
    const toCreate = wanted.filter(
      (id) => statusById.get(id) === "NOT_CREATED",
    );
    if (toCreate.length === 0) {
      return;
    }
    return this._createRendition(
      nodeId,
      toCreate.map((id) => ({ id })),
    );
  }

  _createRendition(nodeId, renditionBodyCreate) {
    return renditionsApi
      .createRendition(nodeId, renditionBodyCreate)
      .catch((error) => {
        if (error.status === 400) {
          let err = JSON.parse(error.message);
          console.warn(err.error?.briefSummary);
        } else if (error.status === 403) {
          console.debug("Current user does not have permission for " + nodeId);
        } else if (error.status === 409) {
          console.debug("All requested renditions already exist");
        } else {
          console.error(error);
        }
      });
  }

  createVersionRendition(nodeId, versionId, rendition) {
    return versionsApi.createVersionRendition(nodeId, versionId, {
      id: rendition,
    });
  }

  getVersionRendition(nodeId, versionId, renditionId) {
    return versionsApi.getVersionRendition(nodeId, versionId, renditionId);
  }

  /**
   * Safely handles thumbnail fallback by hiding the image container
   * and showing the fallback icon. Uses requestAnimationFrame to avoid
   * blocking the main thread during Vue's render cycle.
   * @param {Event} event - The error event from the image element
   */
  handleThumbnailFallback(event) {
    const img = event?.target;
    if (!img) {
      return;
    }

    requestAnimationFrame(() => {
      const parent = img.parentElement;
      if (parent) {
        parent.style.display = "none";
        const sibling = parent.nextElementSibling;
        if (sibling) {
          sibling.hidden = false;
        }
      }
    });
  }

  /**
   * Handles image load failures by showing a fallback icon and optionally
   * requesting rendition creation. Includes deduplication to prevent
   * cascading API calls when many images fail simultaneously.
   * @param {Event} event - The error event from the image element
   * @param {Object} node - The Alfresco node object
   */
  onImageLoadFailure(event, node) {
    this.handleThumbnailFallback(event);

    if (!this.isThumbnailSupported(node)) {
      return;
    }

    const nodeId = node.id;

    // Skip if already processing or recently failed
    if (
      this.pendingRenditions.has(nodeId) ||
      this.failedRenditions.has(nodeId)
    ) {
      return;
    }

    this.pendingRenditions.add(nodeId);

    this.createMissingRenditions(nodeId)
      .catch((err) => {
        this.failedRenditions.set(nodeId, true);
        if (err?.status === 403) {
          console.debug(`Permission denied for rendition on ${nodeId}`);
        } else {
          console.warn(
            `createMissingRenditions error ${err?.status} for ${nodeId}`,
          );
        }
      })
      .finally(() => {
        this.pendingRenditions.delete(nodeId);
      });
  }

  isThumbnailSupported(node) {
    let allowMt = ["application/pdf", "application/msword", "application/rtf"];
    let noTransfomer = ["image/svg+xml"];
    return (
      node.isFile &&
      node.content.mimeType &&
      !noTransfomer.includes(node.content.mimeType) &&
      (node.content.mimeType.startsWith("image/") ||
        node.content.mimeType.startsWith("video/") ||
        node.content.mimeType.startsWith("application/vnd") ||
        allowMt.includes(node.content.mimeType))
    );
  }

  /**
   * Status of a rendition from a listRenditions/listVersionRenditions response.
   * @param paging RenditionPaging response
   * @param rendition the rendition id (e.g. "pdf")
   * @returns {string|null} "CREATED" / "NOT_CREATED", or null when the rendition
   *   is not applicable to the node (i.e. cannot be produced for its mimetype)
   */
  _findRenditionStatus(paging, rendition) {
    const found = paging?.list?.entries?.find((e) => e.entry?.id === rendition);
    return found ? found.entry.status : null;
  }

  /**
   * Build the error thrown when a rendition cannot be produced for a node.
   * Callers can detect it via `error.code === "RENDITION_NOT_AVAILABLE"` to
   * show the default icon / a download-only view instead of retrying.
   */
  _renditionNotAvailable(nodeId, rendition) {
    const error = new Error(
      `Rendition "${rendition}" is not available for ${nodeId}`,
    );
    error.code = "RENDITION_NOT_AVAILABLE";
    return error;
  }

  /**
   * Poll a not-yet-created rendition until it becomes CREATED, then resolve its
   * content URL. Rejects once retries are exhausted.
   * @param getRendition () => Promise<RenditionEntry>
   * @param getUrl () => string content URL once the rendition is ready
   * @param rendition the rendition id, for logging
   */
  _waitForRendition(getRendition, getUrl, rendition) {
    return retry(
      async () => {
        const result = await getRendition();
        if (result.entry.status === "CREATED") {
          return getUrl();
        }
        throw new Error(`Rendition "${rendition}" not ready`);
      },
      {
        retries: 5,
        factor: 2,
        minTimeout: 1000,
        maxTimeout: 15000,
        randomize: false,
        onRetry: (error) => {
          console.debug(
            `Waiting for rendition "${rendition}": ${error.message}`,
          );
        },
      },
    );
  }

  /**
   * Resolve the content URL of a node rendition.
   *
   * Lists the renditions applicable to the node first: listRenditions returns
   * every rendition *possible* for the node's source mimetype, each with a
   * status (CREATED / NOT_CREATED). We decide up-front instead of blindly
   * requesting a rendition for a type that cannot produce it (which only
   * generated errors and log noise):
   * - rendition absent from the list -> reject with RENDITION_NOT_AVAILABLE
   * - CREATED                        -> return its content URL
   * - NOT_CREATED                    -> request creation, then wait until ready
   *
   * @param nodeId
   * @param rendition default "pdf"
   * @returns {Promise<string>} the rendition content URL
   */
  async getRenditionUrl(nodeId, rendition = "pdf") {
    const paging = await renditionsApi.listRenditions(nodeId);
    const status = this._findRenditionStatus(paging, rendition);
    if (status === null) {
      throw this._renditionNotAvailable(nodeId, rendition);
    }
    if (status === "CREATED") {
      return contentApi.getRenditionUrl(nodeId, rendition);
    }
    // Possible but not created yet: request it, then wait for it to be ready.
    await this.createRendition(nodeId, rendition);
    return this._waitForRendition(
      () => renditionsApi.getRendition(nodeId, rendition),
      () => contentApi.getRenditionUrl(nodeId, rendition),
      rendition,
    );
  }

  /**
   * Resolve the content URL of a version rendition. Same strategy as
   * {@link getRenditionUrl}, using the version rendition endpoints.
   *
   * @param nodeId
   * @param versionId
   * @param rendition default "pdf"
   * @returns {Promise<string>} the rendition content URL
   */
  async getVersionRenditionUrl(nodeId, versionId, rendition = "pdf") {
    const paging = await versionsApi.listVersionRenditions(nodeId, versionId);
    const status = this._findRenditionStatus(paging, rendition);
    if (status === null) {
      throw this._renditionNotAvailable(nodeId, rendition);
    }
    if (status === "CREATED") {
      return contentApi.getVersionRenditionUrl(nodeId, versionId, rendition);
    }
    // Possible but not created yet: request it, then wait for it to be ready.
    // Tolerate 409 (already requested) and let the polling decide.
    await this.createVersionRendition(nodeId, versionId, rendition).catch(
      () => {},
    );
    return this._waitForRendition(
      () => versionsApi.getVersionRendition(nodeId, versionId, rendition),
      () => contentApi.getVersionRenditionUrl(nodeId, versionId, rendition),
      rendition,
    );
  }
  getProtocolForFileExtension(fileExtension) {
    const supportedExtensions = {
      doc: "ms-word",
      docx: "ms-word",
      docm: "ms-word",
      dot: "ms-word",
      dotx: "ms-word",
      dotm: "ms-word",
      rtf: "ms-word",
      xls: "ms-excel",
      xlsx: "ms-excel",
      xlsb: "ms-excel",
      xlsm: "ms-excel",
      xlt: "ms-excel",
      xltx: "ms-excel",
      xltm: "ms-excel",
      xlam: "ms-excel",
      ppt: "ms-powerpoint",
      pptx: "ms-powerpoint",
      pot: "ms-powerpoint",
      potx: "ms-powerpoint",
      potm: "ms-powerpoint",
      pptm: "ms-powerpoint",
      pps: "ms-powerpoint",
      ppsx: "ms-powerpoint",
      ppam: "ms-powerpoint",
      ppsm: "ms-powerpoint",
      sldx: "ms-powerpoint",
      sldm: "ms-powerpoint",
      vsd: "ms-visio",
      vss: "ms-visio",
      vst: "ms-visio",
      vsdx: "ms-visio",
      vsdm: "ms-visio",
      vssx: "ms-visio",
      vssm: "ms-visio",
      vstx: "ms-visio",
      vstm: "ms-visio",
    };
    return supportedExtensions[fileExtension];
  }
  getFileExtension(fileName) {
    if (fileName) {
      const match = fileName.match(/\.([^./?#]+)($|\?|#)/);
      return match ? match[1] : null;
    }
    return null;
  }
  isDoc(item) {
    return (
      item.isFile &&
      item.content.mimeType !== "application/pdf" &&
      !item.content.mimeType.includes("image/") &&
      !item.content.mimeType.includes("video/")
    );
  }
  toggleMSOfficeVue(node) {
    if (node.isLocked) {
      const checkedOut =
        node.properties["cm:lockType"] === "WRITE_LOCK" ||
        node.properties["cm:lockType"] === "READ_ONLY_LOCK";
      const lockOwner = node.properties["cm:lockOwner"];
      const differentLockOwner =
        lockOwner.id !== localStorage.getItem("ACS_USERNAME");
      if (checkedOut && differentLockOwner) {
        this.$toast.add({
          severity: "error",
          summary: this.$t("node.locked"),
          detail: this.$t("node.lockedDetails"),
          life: 3000,
        });
      } else {
        this.triggerEditOnlineAos(node);
      }
    } else {
      this.triggerEditOnlineAos(node);
    }
  }
  canOpenWithMSOffice(item) {
    const canUpdate = item.allowableOperations?.includes("update") || false;
    const config = useConfigStore();
    if (item.isFolder) {
      return false;
    }
    return (
      config.EDIT_WITH_MSOFFICE &&
      this.isMSOfficeDoc(item) &&
      canUpdate &&
      (this.isWindows() || this.isMacOs())
    );
  }
  triggerEditOnlineAos(node) {
    const config = useConfigStore();
    let aosHost = `${config.ALFRESCO_HOST}/${config.ALFRESCO_ROOT}/aos`;
    let root = "Sites";
    let url = "";
    if (node.path.name.length > 256) {
      url = `${aosHost}/${root}/_aos_nodeid/${node.id}/${encodeURIComponent(
        node.name,
      )}`;
    } else {
      // Build path from elements, skipping the root folder (locale-dependent name)
      let path = node.path.elements
        .slice(1)
        .map((e) => e.name)
        .join("/");
      url = `${aosHost}/${path}/${node.name}`;
    }
    let fileExtension = alfrescoFileService.getFileExtension(node.name);
    let protocolHandler =
      alfrescoFileService.getProtocolForFileExtension(fileExtension);

    if (protocolHandler === undefined) {
      console.warn(`No protocol handler found for ${fileExtension}`);
      return;
    }

    if (!this.isWindows() && !this.isMacOs()) {
      console.log("Only supported for Windows and Mac");
    } else {
      this.aos_tryToLaunchOfficeByMsProtocolHandler(protocolHandler, url);
    }
  }
  aos_tryToLaunchOfficeByMsProtocolHandler(protocolHandler, url) {
    let protocolUrl = `${protocolHandler}:ofe%7Cu%7C${url}`;

    let iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = protocolUrl;

    document.body.appendChild(iframe);

    setTimeout(() => {
      if (iframe) {
        document.body.removeChild(iframe);
      }
    }, 500);
  }
  isWindows() {
    return this.getUserAgent().indexOf("win") !== -1;
  }
  isMacOs() {
    return this.getUserAgent().indexOf("mac") !== -1;
  }
  getUserAgent() {
    return navigator.userAgent.toLowerCase();
  }
  isMSOfficeDoc(item) {
    const officeMimeTypes = [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.template",
      "application/vnd.ms-word.document.macroEnabled.12",
      "application/vnd.ms-word.template.macroEnabled.12",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.template",
      "application/vnd.ms-excel.sheet.macroEnabled.12",
      "application/vnd.ms-excel.template.macroEnabled.12",
      "application/vnd.ms-excel.addin.macroEnabled.12",
      "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.openxmlformats-officedocument.presentationml.template",
      "application/vnd.openxmlformats-officedocument.presentationml.slideshow",
      "application/vnd.ms-powerpoint.addin.macroEnabled.12",
      "application/vnd.ms-powerpoint.presentation.macroEnabled.12",
      "application/vnd.ms-powerpoint.template.macroEnabled.12",
      "application/vnd.ms-powerpoint.slideshow.macroEnabled.12",
      "application/msonenote",
      "application/vnd.ms-officetheme",
      // OpenDocument formats (LibreOffice)
      "application/vnd.oasis.opendocument.text",
      "application/vnd.oasis.opendocument.text-template",
      "application/vnd.oasis.opendocument.spreadsheet",
      "application/vnd.oasis.opendocument.spreadsheet-template",
      "application/vnd.oasis.opendocument.presentation",
      "application/vnd.oasis.opendocument.presentation-template",
      "application/vnd.oasis.opendocument.graphics",
      "application/vnd.oasis.opendocument.graphics-template",
    ];
    return (
      item.content?.mimeType && officeMimeTypes.includes(item.content.mimeType)
    );
  }
  /**
   * Gère un upload de fichier unique avec suivi de progression.
   * @param {string} nodeId - L'ID du noeud parent où uploader.
   * @param {object} opts - Les options pour l'API Alfresco.
   * @param {File} file - Le fichier à uploader.
   * @returns {Promise} Une promesse qui se résout avec la réponse de l'API.
   */
  uploadFileWithProgress(nodeId, opts, file) {
    const fileState = useUploadStore().files.find((f) => f.name === file.name);
    return uploadApi
      .uploadFile(file, null, nodeId, null, opts)
      .on("progress", (progress) => {
        fileState.percent = progress.percent;
      });
  }

  /**
   * Génère le contenu du tooltip pour un nœud verrouillé en fonction de son type de lock.
   *
   * Utilise les traductions i18n définies dans le namespace `lock.*` :
   * - lock.nodeLock
   * - lock.readOnlyLock
   * - lock.writeLock
   *
   * Chaque traduction reçoit en paramètre l'owner du lock (displayName ou id).
   *
   * @param {Object} node - Le nœud concerné.
   * @param {Object} node.properties - Propriétés du nœud.
   * @param {string} node.properties["cm:lockType"] - Type de verrou appliqué au nœud.
   *        Peut être :
   *        - "NODE_LOCK"       → Personne ne peut mettre à jour ou supprimer le nœud.
   *        - "READ_ONLY_LOCK"  → Personne ne peut mettre à jour/supprimer le nœud, ni ajouter d'enfants.
   *        - "WRITE_LOCK"      → Seul le propriétaire peut modifier, supprimer ou ajouter des enfants.
   * @param {Object} [node.properties["cm:lockOwner"]] - Informations sur le propriétaire du lock.
   * @param {string} [node.properties["cm:lockOwner"].displayName] - Nom affiché du propriétaire.
   * @param {string} [node.properties["cm:lockOwner"].id] - Identifiant du propriétaire si `displayName` est absent.
   *
   * @returns {string} - Message formaté pour affichage dans le tooltip.
   *                     Retourne une traduction i18n si le lockType est reconnu,
   *                     sinon un message générique avec le type et le propriétaire.
   */
  tooltipLockContent(node) {
    const lockType = node.properties["cm:lockType"];
    const owner =
      node.properties["cm:lockOwner"]?.displayName ??
      node.properties["cm:lockOwner"]?.id;
    switch (lockType) {
      case "NODE_LOCK":
        return t("lock.nodeLock", { owner: owner });
      case "READ_ONLY_LOCK":
        return t("lock.readOnlyLock", { owner: owner });
      case "WRITE_LOCK":
        return t("lock.writeLock", { owner: owner });
      default:
        return `Verrou inconnu (${lockType}).\nLock owner: ${owner}`;
    }
  }

  /**
   * Filter predicate for path elements, skipping the root folder
   * (whose name is locale-dependent) and other system nodes.
   * Designed to be used with Array.filter() on node.path.elements.
   */
  isPathElementVisible(item, index) {
    return (
      index > 0 &&
      item.nodeType !== "st:sites" &&
      item.nodeType !== "sys:container" &&
      item.nodeType !== "cm:person" &&
      item.name !== "documentLibrary"
    );
  }
}

export default new AlfrescoFileService();
